import type { PageTrim } from '@rh/shared/utils/content-trim'

/**
 * DOM mirror of the composer's trim geometry (pdf-composer.ts): the source
 * page is cropped by `trim` and fit-scaled into the placement rect. The
 * preview renders the FULL page via pdf.js at `scale`, inside a crop box of
 * the trimmed size, shifted up by the top trim — pixel-equivalent to the
 * server's embedPage bounding box.
 *
 * Units: `rect` is in design px (the 816×1056 chrome coordinate space),
 * `pageSize`/`trim` in PDF points; `scale` is therefore px-per-pt, exactly
 * like the untrimmed `Math.min(r.width / s.w, r.height / s.h)` it replaces.
 */
export function computePreviewCrop(
  rect: { width: number; height: number },
  pageSize: { w: number; h: number },
  trim: PageTrim | null
): { scale: number; cropWidth: number; cropHeight: number; offsetY: number } {
  const topPt = trim?.topPt ?? 0
  const bottomPt = trim?.bottomPt ?? 0
  const croppedH = pageSize.h - topPt - bottomPt
  const scale = Math.min(rect.width / pageSize.w, rect.height / croppedH)
  return {
    scale,
    cropWidth: pageSize.w * scale,
    cropHeight: croppedH * scale,
    offsetY: topPt === 0 ? 0 : -topPt * scale,
  }
}
